import { createFileRoute } from "@tanstack/react-router";
import { ListTodo, Timer, Workflow } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/pipelines-hero.webp";
import boardImg from "@/assets/pipelines-board.webp";
import autoImg from "@/assets/pipelines-auto.webp";

const IMG: Record<string, string> = { hero: heroImg, board: boardImg, auto: autoImg };

export const Route = createFileRoute("/vi/platform/pipelines")({
  head: () => ({
    meta: [
      { title: "Pipeline & Công việc — banana.cards" },
      { name: "description", content: "Xây pipeline, gán việc, tự động hóa bước tiếp theo — chốt deal nhanh hơn." },
      { property: "og:title", content: "Pipeline & Công việc — banana.cards" },
      { property: "og:description", content: "Xây pipeline, gán việc, tự động hóa bước tiếp theo — chốt deal nhanh hơn." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/pipelines", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Pipeline & Công việc"
      title="Đẩy mọi deal"
      italic="tiến lên."
      description="Xây pipeline khớp cách bạn thực sự bán hàng. Kéo deal giữa các giai đoạn, gán việc, đặt nhắc nhở, và để tự động hóa đẩy phần còn lại."
      heroImage={IMG['hero']}
      metrics={[{ v: "Không giới hạn", l: "pipeline" }, { v: "Tự động", l: "bước tiếp theo" }, { v: "3x", l: "thông lượng" }]}
      bullets={[
        { icon: Workflow, title: "Pipeline tùy chỉnh", desc: "Theo đội, theo sản phẩm, theo vùng — và đổi view một cú nhấp." },
        { icon: ListTodo, title: "Việc cần làm nhắc nhở", desc: "Tự gán, tự đặt hạn, với nhắc thực sự kích hoạt." },
        { icon: Timer, title: "Theo dõi SLA", desc: "Xem deal đang kẹt ở đâu và nơi nhân viên cần giúp." }
      ]}
      deepDives={[
        { eyebrow: "Bảng", title: "Kéo, thả, xong.", italic: "Kanban hoặc danh sách.", description: "Chuyển giữa view bảng, danh sách và timeline mà không mất ngữ cảnh. Sửa hàng loạt hàng trăm deal trong vài giây.", image: IMG["board"], reverse: false, points: ["View Kanban, danh sách và timeline", "Sửa hàng loạt và gán", "Trường deal tùy chỉnh và công thức", "Yêu cầu giai đoạn có điều kiện"] },
        { eyebrow: "Tự động hóa", title: "Đẩy việc", italic: "khi bạn ngủ.", description: "Khi deal vào 'đã gửi đề xuất', tự tạo việc theo dõi, lên nhắc và báo nhân viên — chỉ từ một trigger.", image: IMG["auto"], reverse: true, points: ["Trigger theo đổi giai đoạn", "Tự tạo công việc", "Thông báo Slack / email", "Phân bổ vòng tròn"] }
      ]}
      howItWorks={[
        { title: "Thiết kế giai đoạn", desc: "Khớp cách bạn thực sự bán." },
        { title: "Thêm deal", desc: "Từ liên hệ, form hoặc nhập." },
        { title: "Tự động hóa phần chán", desc: "Việc, nhắc, thông báo." },
        { title: "Theo dõi và huấn luyện", desc: "SLA, vận tốc, deal kẹt." }
      ]}
      capabilities={[
        "Pipeline không giới hạn",
        "Giai đoạn và trường tùy chỉnh",
        "Việc với SLA",
        "Phân bổ vòng tròn",
        "Yêu cầu giai đoạn",
        "Hành động hàng loạt",
        "Báo cáo vận tốc & phễu",
        "API & webhook"
      ]}
      faq={[
        { q: "Thực sự có thể theo dõi gì trong một pipeline?", a: "Bất cứ thứ gì di chuyển qua các giai đoạn: deal bán hàng, onboarding khách, tuyển dụng (ứng viên → đề nghị → đã thuê), giao dự án, đàm phán đối tác, ticket hỗ trợ, kể cả sản xuất nội dung. Mỗi pipeline có giai đoạn tùy chỉnh đầy đủ, trường tùy chỉnh theo card, và giá trị deal có tiền tệ — nên cùng workspace có thể chạy pipeline Bán hàng và Tuyển dụng song song." },
        { q: "Có thể tạo bao nhiêu pipeline và lớn cỡ nào?", a: "Tạo bao nhiêu pipeline tùy giới hạn gói, với giai đoạn không giới hạn và card không giới hạn theo giai đoạn. Kanban kéo-thả xử lý hàng nghìn card mượt mà nhờ rendering ảo hóa." },
        { q: "Có thể gắn liên hệ, việc và ghi chú vào card deal không?", a: "Có. Mỗi card liên kết một hoặc nhiều liên hệ CRM, mang danh sách việc với hạn và người được gán, hỗ trợ ghi chú và đính kèm, và hiển thị timeline hoạt động đầy đủ. Đóng card ghi kết quả vào từng liên hệ liên kết." },
        { q: "Có thể dự báo doanh thu và đánh trọng số deal không?", a: "Có — gán giá trị và tiền tệ cho mỗi card, đặt xác suất theo giai đoạn (ví dụ 'Đàm phán' 70%) và Banana cuộn tổng dự báo có trọng số theo pipeline, người được gán và khoảng thời gian. Chuyển đổi FX sang tiền tệ tài khoản tự động." },
        { q: "Có hoạt động đa tiền tệ không?", a: "Có — ghi mỗi deal theo tiền tệ gốc (USD, EUR, GBP, VND, v.v.) và Banana chuyển sang tiền tệ báo cáo của workspace dùng tỷ giá FX trực tiếp." },
        { q: "Có thể phân bổ deal mới theo vòng tròn cho đội không?", a: "Có — đặt người được gán mặc định theo pipeline hoặc vòng tròn qua một nhóm, và kích hoạt tự động hóa khi đổi giai đoạn (ví dụ khi deal vào 'Won', tự tạo hợp đồng onboarding và việc kickoff)." }
      ]}
      outro={ { title: "Đừng chạy theo.", italic: "Hãy chốt deal.", description: "Pipeline và công việc thực sự đẩy việc tiến lên." } }
    />
  );
}